import { useCallback } from 'react';
import { ShapeInstance } from '../../services/api';
import {
  ShapeType,
  ShapeParams,
  ShapeToolSettings,
  ArrowStyle,
  CalloutPointerPosition,
  RectangleParams,
  PolygonParams,
  StarParams,
  ArrowParams,
  CalloutParams
} from '../../types/shape';
import {
  LineCap,
  LineJoin,
  DashPreset
} from '../../types/penPath';
import Checkbox from '../../components/Checkbox';
import './ShapePanel.css';

interface ShapePanelProps {
  settings: ShapeToolSettings;
  onSettingsChange: (settings: ShapeToolSettings) => void;
  selectedShapeInstance: ShapeInstance | null;
  selectedCount?: number;
  onUpdateShape: (updates: Partial<ShapeInstance>) => void;
  onDeleteShape: () => void;
  isDrawingShape?: boolean;
}

/**
 * Shape type button icons
 */
const SHAPE_ICONS: Record<ShapeType, string> = {
  rectangle: 'fa-square',
  ellipse: 'fa-circle',
  polygon: 'fa-hexagon-vertical-nft',
  star: 'fa-star',
  arrow: 'fa-arrow-right',
  cross: 'fa-plus',
  diamond: 'fa-diamond',
  callout: 'fa-comment'
};

/**
 * Shape type labels
 */
const SHAPE_LABELS: Record<ShapeType, string> = {
  rectangle: 'Rectangle',
  ellipse: 'Ellipse',
  polygon: 'Polygon',
  star: 'Star',
  arrow: 'Arrow',
  cross: 'Cross',
  diamond: 'Diamond',
  callout: 'Callout'
};

/**
 * LineCap icon component
 */
function LineCapIcon({ cap, active }: { cap: LineCap; active: boolean }): JSX.Element {
  const color = active ? '#000' : 'currentColor';

  return (
    <svg viewBox="0 0 24 16" className="shape-panel-cap-icon">
      <line x1="2" y1="8" x2="22" y2="8" stroke={color} strokeWidth="4"
        strokeLinecap={cap} />
    </svg>
  );
}

/**
 * LineJoin icon component
 */
function LineJoinIcon({ join, active }: { join: LineJoin; active: boolean }): JSX.Element {
  const color = active ? '#000' : 'currentColor';

  return (
    <svg viewBox="0 0 24 16" className="shape-panel-join-icon">
      <polyline points="2,14 12,2 22,14" fill="none" stroke={color} strokeWidth="3"
        strokeLinejoin={join} strokeLinecap="round" />
    </svg>
  );
}

/**
 * Panel for shape tool settings and selected shape editing
 */
function ShapePanel({
  settings,
  onSettingsChange,
  selectedShapeInstance,
  selectedCount = 0,
  onUpdateShape,
  onDeleteShape,
  isDrawingShape: _isDrawingShape = false
}: ShapePanelProps): JSX.Element {

  /**
   * Handle setting change (for new shape defaults)
   */
  const handleSettingChange = useCallback(<K extends keyof ShapeToolSettings>(
    key: K,
    value: ShapeToolSettings[K]
  ) => {
    onSettingsChange({ ...settings, [key]: value });
  }, [settings, onSettingsChange]);

  /**
   * Handle selected shape update
   */
  const handleShapeUpdate = useCallback(<K extends keyof ShapeInstance>(
    key: K,
    value: ShapeInstance[K]
  ) => {
    onUpdateShape({ [key]: value });
  }, [onUpdateShape]);

  /**
   * Update shape params for selected shape
   */
  const updateShapeParams = useCallback((paramUpdates: Record<string, unknown>) => {
    if (!selectedShapeInstance) return;

    const currentParams = selectedShapeInstance.shapeParams.params;
    const updatedParams = { ...currentParams, ...paramUpdates };

    onUpdateShape({
      shapeParams: {
        type: selectedShapeInstance.shapeType,
        params: updatedParams
      } as ShapeParams
    });
  }, [selectedShapeInstance, onUpdateShape]);

  /**
   * Render shape type selector
   */
  const renderShapeTypeSelector = () => (
    <div className="shape-panel-control">
      <div className="shape-panel-type-grid">
        {(Object.keys(SHAPE_ICONS) as ShapeType[]).map((type) => (
          <button
            key={type}
            className={`shape-panel-type-btn ${settings.selectedShapeType === type ? 'shape-panel-type-btn--active' : ''}`}
            onClick={() => handleSettingChange('selectedShapeType', type)}
            title={SHAPE_LABELS[type]}
          >
            <i className={`fas ${SHAPE_ICONS[type]}`}></i>
          </button>
        ))}
      </div>
    </div>
  );

  /**
   * Render shape-specific parameters
   */
  const renderShapeParameters = (isSelected: boolean) => {
    const shapeType = isSelected && selectedShapeInstance
      ? selectedShapeInstance.shapeType
      : settings.selectedShapeType;

    switch (shapeType) {
      case 'rectangle':
        if (isSelected && selectedShapeInstance) {
          const params = selectedShapeInstance.shapeParams.params as RectangleParams;
          return (
            <div className="shape-panel-control">
              <label className="shape-panel-label">Corner Radius</label>
              <input
                type="number"
                className="shape-panel-input"
                value={params.cornerRadius?.topLeft ?? 0}
                onChange={(e) => {
                  const val = Math.max(0, parseFloat(e.target.value) || 0);
                  updateShapeParams({
                    cornerRadius: {
                      ...params.cornerRadius,
                      topLeft: val,
                      topRight: val,
                      bottomRight: val,
                      bottomLeft: val,
                      uniform: true
                    }
                  });
                }}
                min={0}
                step={1}
              />
              <span className="shape-panel-unit">px</span>
            </div>
          );
        }
        return (
          <div className="shape-panel-control">
            <label className="shape-panel-label">Corner Radius</label>
            <input
              type="number"
              className="shape-panel-input"
              value={settings.rectangleCornerRadius}
              onChange={(e) => {
                const val = Math.max(0, parseFloat(e.target.value) || 0);
                handleSettingChange('rectangleCornerRadius', val);
              }}
              min={0}
              step={1}
            />
            <span className="shape-panel-unit">px</span>
          </div>
        );

      case 'polygon':
        if (isSelected && selectedShapeInstance) {
          const params = selectedShapeInstance.shapeParams.params as PolygonParams;
          return (
            <div className="shape-panel-control">
              <label className="shape-panel-label">Sides</label>
              <input
                type="number"
                className="shape-panel-input"
                value={params.sides}
                onChange={(e) => {
                  const val = Math.max(3, Math.min(60, parseInt(e.target.value) || 6));
                  updateShapeParams({ sides: val });
                }}
                min={3}
                max={60}
                step={1}
              />
            </div>
          );
        }
        return (
          <div className="shape-panel-control">
            <label className="shape-panel-label">Sides</label>
            <input
              type="number"
              className="shape-panel-input"
              value={settings.polygonSides}
              onChange={(e) => {
                const val = Math.max(3, Math.min(60, parseInt(e.target.value) || 6));
                handleSettingChange('polygonSides', val);
              }}
              min={3}
              max={60}
              step={1}
            />
          </div>
        );

      case 'star':
        if (isSelected && selectedShapeInstance) {
          const params = selectedShapeInstance.shapeParams.params as StarParams;
          return (
            <>
              <div className="shape-panel-control">
                <label className="shape-panel-label">Points</label>
                <input
                  type="number"
                  className="shape-panel-input"
                  value={params.points}
                  onChange={(e) => {
                    const val = Math.max(3, Math.min(60, parseInt(e.target.value) || 5));
                    updateShapeParams({ points: val });
                  }}
                  min={3}
                  max={60}
                  step={1}
                />
              </div>
              <div className="shape-panel-control">
                <label className="shape-panel-label">Inner Ratio</label>
                <div className="shape-panel-slider-row">
                  <input
                    type="range"
                    className="shape-panel-slider"
                    min={0.1}
                    max={0.9}
                    step={0.05}
                    value={params.innerRatio}
                    onChange={(e) => {
                      updateShapeParams({ innerRatio: parseFloat(e.target.value) });
                    }}
                  />
                  <span className="shape-panel-slider-value">
                    {Math.round(params.innerRatio * 100)}%
                  </span>
                </div>
              </div>
            </>
          );
        }
        return (
          <>
            <div className="shape-panel-control">
              <label className="shape-panel-label">Points</label>
              <input
                type="number"
                className="shape-panel-input"
                value={settings.starPoints}
                onChange={(e) => {
                  const val = Math.max(3, Math.min(60, parseInt(e.target.value) || 5));
                  handleSettingChange('starPoints', val);
                }}
                min={3}
                max={60}
                step={1}
              />
            </div>
            <div className="shape-panel-control">
              <label className="shape-panel-label">Inner Ratio</label>
              <div className="shape-panel-slider-row">
                <input
                  type="range"
                  className="shape-panel-slider"
                  min={0.1}
                  max={0.9}
                  step={0.05}
                  value={settings.starInnerRatio}
                  onChange={(e) => {
                    handleSettingChange('starInnerRatio', parseFloat(e.target.value));
                  }}
                />
                <span className="shape-panel-slider-value">
                  {Math.round(settings.starInnerRatio * 100)}%
                </span>
              </div>
            </div>
          </>
        );

      case 'arrow':
        if (isSelected && selectedShapeInstance) {
          const params = selectedShapeInstance.shapeParams.params as ArrowParams;
          return (
            <div className="shape-panel-control">
              <label className="shape-panel-label">Style</label>
              <select
                className="shape-panel-select"
                value={params.style}
                onChange={(e) => {
                  updateShapeParams({ style: e.target.value as ArrowStyle });
                }}
              >
                <option value="block">Block</option>
                <option value="chevron">Chevron</option>
                <option value="pointer">Pointer</option>
                <option value="double">Double</option>
                <option value="curved">Curved</option>
              </select>
            </div>
          );
        }
        return (
          <div className="shape-panel-control">
            <label className="shape-panel-label">Style</label>
            <select
              className="shape-panel-select"
              value={settings.arrowStyle}
              onChange={(e) => {
                handleSettingChange('arrowStyle', e.target.value as ArrowStyle);
              }}
            >
              <option value="block">Block</option>
              <option value="chevron">Chevron</option>
              <option value="pointer">Pointer</option>
              <option value="double">Double</option>
              <option value="curved">Curved</option>
            </select>
          </div>
        );

      case 'callout':
        if (isSelected && selectedShapeInstance) {
          const params = selectedShapeInstance.shapeParams.params as CalloutParams;
          return (
            <div className="shape-panel-control">
              <label className="shape-panel-label">Pointer</label>
              <select
                className="shape-panel-select"
                value={params.pointerPosition}
                onChange={(e) => {
                  updateShapeParams({ pointerPosition: e.target.value as CalloutPointerPosition });
                }}
              >
                <option value="top">Top</option>
                <option value="right">Right</option>
                <option value="bottom">Bottom</option>
                <option value="left">Left</option>
              </select>
            </div>
          );
        }
        return (
          <div className="shape-panel-control">
            <label className="shape-panel-label">Pointer</label>
            <select
              className="shape-panel-select"
              value={settings.calloutPointerPosition}
              onChange={(e) => {
                handleSettingChange('calloutPointerPosition', e.target.value as CalloutPointerPosition);
              }}
            >
              <option value="top">Top</option>
              <option value="right">Right</option>
              <option value="bottom">Bottom</option>
              <option value="left">Left</option>
            </select>
          </div>
        );

      default:
        return null;
    }
  };

  /**
   * Render stroke controls
   */
  const renderStrokeControls = (
    currentStrokeColor: string | null,
    currentStrokeWidth: number,
    currentStrokeOpacity: number,
    currentLineCap: LineCap,
    currentLineJoin: LineJoin,
    currentDashPreset: DashPreset,
    isSelected: boolean,
    currentFillColor: string | null
  ) => (
    <>
      {/* Stroke Enable/Color */}
      <div className="shape-panel-control">
        <label className="shape-panel-label">Stroke</label>
        <div className="shape-panel-color-row">
          <Checkbox
            label=""
            checked={currentStrokeColor !== null}
            onChange={(checked) => {
              if (!checked && currentFillColor === null) {
                if (isSelected) {
                  handleShapeUpdate('fillColor', '#3388ff');
                } else {
                  handleSettingChange('fillColor', '#3388ff');
                }
              }
              const val = checked ? '#000000' : null;
              isSelected
                ? handleShapeUpdate('strokeColor', val)
                : handleSettingChange('strokeColor', val as string);
            }}
          />
          {currentStrokeColor !== null && (
            <>
              <input
                type="color"
                className="shape-panel-color"
                value={currentStrokeColor}
                onChange={(e) => isSelected
                  ? handleShapeUpdate('strokeColor', e.target.value)
                  : handleSettingChange('strokeColor', e.target.value)
                }
              />
              <input
                type="text"
                className="shape-panel-color-input"
                value={currentStrokeColor}
                onChange={(e) => isSelected
                  ? handleShapeUpdate('strokeColor', e.target.value)
                  : handleSettingChange('strokeColor', e.target.value)
                }
              />
            </>
          )}
        </div>
      </div>

      {currentStrokeColor !== null && (
        <>
          {/* Stroke Width */}
          <div className="shape-panel-control">
            <label className="shape-panel-label">Width</label>
            <input
              type="number"
              className="shape-panel-input"
              value={currentStrokeWidth}
              onChange={(e) => {
                const val = Math.max(0.5, Math.min(50, parseFloat(e.target.value) || 1));
                isSelected
                  ? handleShapeUpdate('strokeWidth', val)
                  : handleSettingChange('strokeWidth', val);
              }}
              min={0.5}
              max={50}
              step={0.5}
            />
            <span className="shape-panel-unit">px</span>
          </div>

          {/* Stroke Opacity */}
          <div className="shape-panel-control">
            <label className="shape-panel-label">Opacity</label>
            <div className="shape-panel-slider-row">
              <input
                type="range"
                className="shape-panel-slider"
                min={0}
                max={1}
                step={0.01}
                value={currentStrokeOpacity}
                onChange={(e) => {
                  const val = parseFloat(e.target.value);
                  isSelected
                    ? handleShapeUpdate('strokeOpacity', val)
                    : handleSettingChange('strokeOpacity', val);
                }}
              />
              <span className="shape-panel-slider-value">
                {Math.round(currentStrokeOpacity * 100)}%
              </span>
            </div>
          </div>

          {/* Dash Pattern */}
          <div className="shape-panel-control">
            <label className="shape-panel-label">Style</label>
            <div className="shape-panel-button-group">
              {(['solid', 'dashed', 'dotted'] as DashPreset[]).map((preset) => (
                <button
                  key={preset}
                  className={`shape-panel-btn ${currentDashPreset === preset ? 'shape-panel-btn--active' : ''}`}
                  onClick={() => isSelected
                    ? handleShapeUpdate('dashPreset', preset)
                    : handleSettingChange('dashPreset', preset)
                  }
                  title={preset.charAt(0).toUpperCase() + preset.slice(1)}
                >
                  {preset === 'solid' && <span style={{ letterSpacing: 0 }}>---</span>}
                  {preset === 'dashed' && <span style={{ letterSpacing: '2px' }}>- -</span>}
                  {preset === 'dotted' && <span style={{ letterSpacing: '3px' }}>...</span>}
                </button>
              ))}
            </div>
          </div>

          {/* Line Cap */}
          <div className="shape-panel-control">
            <label className="shape-panel-label">Cap</label>
            <div className="shape-panel-button-group">
              {(['butt', 'round', 'square'] as LineCap[]).map((cap) => (
                <button
                  key={cap}
                  className={`shape-panel-btn shape-panel-btn--icon-only ${currentLineCap === cap ? 'shape-panel-btn--active' : ''}`}
                  onClick={() => isSelected
                    ? handleShapeUpdate('lineCap', cap)
                    : handleSettingChange('lineCap', cap)
                  }
                  title={cap.charAt(0).toUpperCase() + cap.slice(1)}
                >
                  <LineCapIcon cap={cap} active={currentLineCap === cap} />
                </button>
              ))}
            </div>
          </div>

          {/* Line Join */}
          <div className="shape-panel-control">
            <label className="shape-panel-label">Join</label>
            <div className="shape-panel-button-group">
              {(['miter', 'round', 'bevel'] as LineJoin[]).map((join) => (
                <button
                  key={join}
                  className={`shape-panel-btn shape-panel-btn--icon-only ${currentLineJoin === join ? 'shape-panel-btn--active' : ''}`}
                  onClick={() => isSelected
                    ? handleShapeUpdate('lineJoin', join)
                    : handleSettingChange('lineJoin', join)
                  }
                  title={join.charAt(0).toUpperCase() + join.slice(1)}
                >
                  <LineJoinIcon join={join} active={currentLineJoin === join} />
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </>
  );

  /**
   * Render fill controls
   */
  const renderFillControls = (
    currentFillColor: string | null,
    currentFillOpacity: number,
    currentFeatherRadius: number,
    currentInvertFill: boolean,
    isSelected: boolean,
    currentStrokeColor: string | null
  ) => (
    <>
      {/* Fill Enable/Color */}
      <div className="shape-panel-control">
        <label className="shape-panel-label">Fill</label>
        <div className="shape-panel-color-row">
          <Checkbox
            label=""
            checked={currentFillColor !== null}
            onChange={(checked) => {
              if (!checked && currentStrokeColor === null) {
                if (isSelected) {
                  handleShapeUpdate('strokeColor', '#000000');
                } else {
                  handleSettingChange('strokeColor', '#000000');
                }
              }
              const val = checked ? '#3388ff' : null;
              isSelected
                ? handleShapeUpdate('fillColor', val)
                : handleSettingChange('fillColor', val);
            }}
          />
          {currentFillColor !== null && (
            <>
              <input
                type="color"
                className="shape-panel-color"
                value={currentFillColor}
                onChange={(e) => isSelected
                  ? handleShapeUpdate('fillColor', e.target.value)
                  : handleSettingChange('fillColor', e.target.value)
                }
              />
              <input
                type="text"
                className="shape-panel-color-input"
                value={currentFillColor}
                onChange={(e) => isSelected
                  ? handleShapeUpdate('fillColor', e.target.value)
                  : handleSettingChange('fillColor', e.target.value)
                }
              />
            </>
          )}
        </div>
      </div>

      {currentFillColor !== null && (
        <>
          <div className="shape-panel-control">
            <label className="shape-panel-label">Opacity</label>
            <div className="shape-panel-slider-row">
              <input
                type="range"
                className="shape-panel-slider"
                min={0}
                max={1}
                step={0.01}
                value={currentFillOpacity}
                onChange={(e) => {
                  const val = parseFloat(e.target.value);
                  isSelected
                    ? handleShapeUpdate('fillOpacity', val)
                    : handleSettingChange('fillOpacity', val);
                }}
              />
              <span className="shape-panel-slider-value">
                {Math.round(currentFillOpacity * 100)}%
              </span>
            </div>
          </div>

          <div className="shape-panel-control">
            <label className="shape-panel-label">Feather</label>
            <div className="shape-panel-slider-row">
              <input
                type="range"
                className="shape-panel-slider"
                min={0}
                max={500}
                step={1}
                value={currentFeatherRadius}
                onChange={(e) => {
                  const val = parseFloat(e.target.value);
                  isSelected
                    ? handleShapeUpdate('featherRadius', val)
                    : handleSettingChange('featherRadius', val);
                }}
              />
              <span className="shape-panel-slider-value">
                {Math.round(currentFeatherRadius)}px
              </span>
            </div>
          </div>

          <div className="shape-panel-control">
            <label className="shape-panel-label">Invert</label>
            <div className="shape-panel-color-row">
              <Checkbox
                label="Cutout mode"
                checked={currentInvertFill}
                onChange={(checked) => {
                  isSelected
                    ? handleShapeUpdate('invertFill', checked)
                    : handleSettingChange('invertFill', checked);
                }}
              />
            </div>
          </div>
        </>
      )}
    </>
  );

  return (
    <div className="shape-panel">
      {/* Selected Shape Instance Controls */}
      {selectedShapeInstance && (
        <div className="shape-panel-section shape-panel-selected">
          <div className="shape-panel-section-title">
            {selectedCount > 1 ? `${selectedCount} Shapes Selected` : `Selected ${SHAPE_LABELS[selectedShapeInstance.shapeType]}`}
            <button
              className="shape-panel-deselect"
              onClick={() => onUpdateShape({})}
              title="Deselect"
            >
              <i className="fas fa-times"></i>
            </button>
          </div>

          {/* Shape-specific parameters */}
          {selectedCount === 1 && (
            <>
              <div className="shape-panel-controls">
                {renderShapeParameters(true)}
              </div>
              <div className="shape-panel-divider" />
            </>
          )}

          {/* Stroke controls for selected shape */}
          <div className="shape-panel-controls">
            {renderStrokeControls(
              selectedShapeInstance.strokeColor,
              selectedShapeInstance.strokeWidth,
              selectedShapeInstance.strokeOpacity,
              selectedShapeInstance.lineCap as LineCap,
              selectedShapeInstance.lineJoin as LineJoin,
              selectedShapeInstance.dashPreset as DashPreset,
              true,
              selectedShapeInstance.fillColor
            )}
          </div>

          <div className="shape-panel-divider" />

          {/* Fill controls for selected shape */}
          <div className="shape-panel-controls">
            {renderFillControls(
              selectedShapeInstance.fillColor,
              selectedShapeInstance.fillOpacity,
              selectedShapeInstance.featherRadius ?? 0,
              selectedShapeInstance.invertFill ?? false,
              true,
              selectedShapeInstance.strokeColor
            )}
          </div>

          {/* Actions */}
          <div className="shape-panel-actions">
            <button
              className="shape-panel-action-btn shape-panel-action-btn--danger"
              onClick={onDeleteShape}
              title={selectedCount > 1 ? `Delete ${selectedCount} Shapes` : 'Delete Shape'}
            >
              <i className="fas fa-trash"></i>
              <span>Delete</span>
            </button>
          </div>
        </div>
      )}

      {/* New Shape Settings */}
      {!selectedShapeInstance && (
        <>
          <div className="shape-panel-section">
            <div className="shape-panel-section-title">
              <span>Shape Type</span>
            </div>
            {renderShapeTypeSelector()}
          </div>

          <div className="shape-panel-section">
            <div className="shape-panel-section-title">
              <span>Parameters</span>
            </div>
            <div className="shape-panel-controls">
              {renderShapeParameters(false)}
            </div>
          </div>

          <div className="shape-panel-section">
            <div className="shape-panel-section-title">
              <span>Stroke</span>
            </div>
            <div className="shape-panel-controls">
              {renderStrokeControls(
                settings.strokeColor,
                settings.strokeWidth,
                settings.strokeOpacity,
                settings.lineCap,
                settings.lineJoin,
                settings.dashPreset,
                false,
                settings.fillColor
              )}
            </div>
          </div>

          <div className="shape-panel-section">
            <div className="shape-panel-section-title">
              <span>Fill</span>
            </div>
            <div className="shape-panel-controls">
              {renderFillControls(
                settings.fillColor,
                settings.fillOpacity,
                settings.featherRadius,
                settings.invertFill,
                false,
                settings.strokeColor
              )}
            </div>
          </div>
        </>
      )}

      {/* Help Text */}
      <div className="shape-panel-help">
        <div className="shape-panel-help-item">
          <kbd>Click + Drag</kbd> Draw shape
        </div>
        <div className="shape-panel-help-item">
          <kbd>Shift</kbd> Constrain proportions
        </div>
        <div className="shape-panel-help-item">
          <kbd>Alt</kbd> Draw from center
        </div>
        <div className="shape-panel-help-item">
          <kbd>Drag corner</kbd> Resize
        </div>
        <div className="shape-panel-help-item">
          <kbd>Drag rotation</kbd> Rotate
        </div>
        <div className="shape-panel-help-item">
          <kbd>Ctrl + C</kbd> Copy selected
        </div>
        <div className="shape-panel-help-item">
          <kbd>Ctrl + X</kbd> Cut selected
        </div>
        <div className="shape-panel-help-item">
          <kbd>Ctrl + V</kbd> Paste at cursor
        </div>
        <div className="shape-panel-help-item">
          <kbd>Del</kbd> Delete selected
        </div>
        <div className="shape-panel-help-item">
          <kbd>Esc</kbd> Cancel / Deselect
        </div>
      </div>
    </div>
  );
}

export default ShapePanel;
